'use client';

import { useState, useEffect } from 'react';
import { UserRequirements } from '@/lib/tech-stack-recommender';
import { TECH_CATEGORIES } from '@/lib/tech-stack-types';

interface QuestionnaireProps {
  onComplete: (requirements: UserRequirements) => void;
  initialData?: Partial<UserRequirements>;
}

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

const STORAGE_KEY = 'tech-stack-questionnaire';

const TEMPLATES = {
  'startup-saas': {
    name: 'Startup SaaS',
    description: 'Early-stage SaaS company, rapid iteration, limited budget',
    data: {
      teamSize: 'startup' as const,
      industry: 'SaaS',
      scalabilityNeeds: 'medium' as const,
      performanceNeeds: 'medium' as const,
      budget: 'minimal' as const,
      teamExpertise: 'intermediate' as const,
      hostingPreference: 'cloud' as const,
      maintenanceCapability: 'minimal' as const,
      gdprRequired: true,
      hipaaRequired: false,
      soc2Required: false,
      pciRequired: false,
      vendorLockInTolerance: 'medium' as const,
      openSourcePreference: 'preferred' as const,
      innovationVsStability: 'modern' as const,
      useCases: ['Web applications', 'Rapid prototyping'],
      mustHaveFeatures: [],
      niceToHaveFeatures: [],
      timeline: 'urgent' as const,
    },
  },
  'enterprise-fintech': {
    name: 'Enterprise FinTech',
    description: 'Financial services, strict compliance, high reliability',
    data: {
      teamSize: 'enterprise' as const,
      industry: 'Finance',
      scalabilityNeeds: 'very-high' as const,
      performanceNeeds: 'very-high' as const,
      budget: 'enterprise' as const,
      teamExpertise: 'expert' as const,
      hostingPreference: 'managed' as const,
      maintenanceCapability: 'high' as const,
      gdprRequired: true,
      hipaaRequired: false,
      soc2Required: true,
      pciRequired: true,
      vendorLockInTolerance: 'low' as const,
      openSourcePreference: 'neutral' as const,
      innovationVsStability: 'stable' as const,
      useCases: ['Transactional systems', 'Enterprise applications'],
      mustHaveFeatures: [],
      niceToHaveFeatures: [],
      timeline: 'flexible' as const,
    },
  },
  'scale-up': {
    name: 'Scale-up',
    description: 'Growing company, scaling infrastructure, moderate budget',
    data: {
      teamSize: 'medium' as const,
      industry: 'Technology',
      scalabilityNeeds: 'high' as const,
      performanceNeeds: 'high' as const,
      budget: 'substantial' as const,
      teamExpertise: 'advanced' as const,
      hostingPreference: 'cloud' as const,
      maintenanceCapability: 'moderate' as const,
      gdprRequired: true,
      hipaaRequired: false,
      soc2Required: true,
      pciRequired: false,
      vendorLockInTolerance: 'medium' as const,
      openSourcePreference: 'preferred' as const,
      innovationVsStability: 'modern' as const,
      useCases: ['Microservices', 'Web applications'],
      mustHaveFeatures: [],
      niceToHaveFeatures: [],
      timeline: 'moderate' as const,
    },
  },
};

export default function TechStackQuestionnaire({ onComplete, initialData }: QuestionnaireProps) {
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [requirements, setRequirements] = useState<Partial<UserRequirements>>(() => {
    if (initialData) return initialData;

    // Try to load from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          return {};
        }
      }
    }
    return {};
  });

  // Save to localStorage on change
  useEffect(() => {
    if (typeof window !== 'undefined' && Object.keys(requirements).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(requirements));
    }
  }, [requirements]);

  const updateRequirement = <K extends keyof UserRequirements>(
    key: K,
    value: UserRequirements[K]
  ) => {
    setRequirements(prev => ({ ...prev, [key]: value }));
  };

  // Calculate total steps dynamically based on conditional questions
  const getTotalSteps = (): number => {
    let total = 8; // Base steps: Org, Timeline, Tech, Priority, Budget, Operational, Strategic, Review

    // Add conditional steps based on answers
    if (requirements.industry === 'Healthcare' || requirements.industry === 'Finance') {
      total += 0; // Compliance details shown inline, not separate step
    }
    if (requirements.scalabilityNeeds === 'very-high') {
      total += 0; // Multi-region shown inline, not separate step
    }
    if (requirements.useCases?.includes('E-commerce')) {
      total += 0; // E-commerce details shown inline, not separate step
    }

    return total;
  };

  const nextStep = () => {
    if (currentStep < 8) {
      setCurrentStep((currentStep + 1) as Step);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as Step);
    }
  };

  const handleSubmit = () => {
    // Ensure all required fields are set
    const completeRequirements: UserRequirements = {
      teamSize: requirements.teamSize || 'small',
      industry: requirements.industry || 'Technology',
      scalabilityNeeds: requirements.scalabilityNeeds || 'medium',
      performanceNeeds: requirements.performanceNeeds || 'medium',
      budget: requirements.budget || 'moderate',
      teamExpertise: requirements.teamExpertise || 'intermediate',
      hostingPreference: requirements.hostingPreference || 'no-preference',
      maintenanceCapability: requirements.maintenanceCapability || 'moderate',
      gdprRequired: requirements.gdprRequired ?? false,
      hipaaRequired: requirements.hipaaRequired ?? false,
      soc2Required: requirements.soc2Required ?? false,
      pciRequired: requirements.pciRequired ?? false,
      vendorLockInTolerance: requirements.vendorLockInTolerance || 'medium',
      openSourcePreference: requirements.openSourcePreference || 'neutral',
      innovationVsStability: requirements.innovationVsStability || 'stable',
      useCases: requirements.useCases || [],
      mustHaveFeatures: requirements.mustHaveFeatures || [],
      niceToHaveFeatures: requirements.niceToHaveFeatures || [],
      // New optional fields
      priorityWeights: requirements.priorityWeights,
      expertiseByCategory: requirements.expertiseByCategory,
      timeline: requirements.timeline,
      currentStack: requirements.currentStack,
      complianceDetails: requirements.complianceDetails,
      multiRegion: requirements.multiRegion,
      regions: requirements.regions,
      transactionVolume: requirements.transactionVolume,
    };

    // Clear localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }

    onComplete(completeRequirements);
  };

  const loadTemplate = (templateKey: keyof typeof TEMPLATES) => {
    const templateData = TEMPLATES[templateKey].data;

    // Templates provide all required fields, so submit directly
    const completeRequirements: UserRequirements = {
      teamSize: templateData.teamSize,
      industry: templateData.industry,
      scalabilityNeeds: templateData.scalabilityNeeds,
      performanceNeeds: templateData.performanceNeeds,
      budget: templateData.budget,
      teamExpertise: templateData.teamExpertise,
      hostingPreference: templateData.hostingPreference,
      maintenanceCapability: templateData.maintenanceCapability,
      gdprRequired: templateData.gdprRequired,
      hipaaRequired: templateData.hipaaRequired,
      soc2Required: templateData.soc2Required,
      pciRequired: templateData.pciRequired,
      vendorLockInTolerance: templateData.vendorLockInTolerance,
      openSourcePreference: templateData.openSourcePreference,
      innovationVsStability: templateData.innovationVsStability,
      useCases: templateData.useCases,
      mustHaveFeatures: templateData.mustHaveFeatures,
      niceToHaveFeatures: templateData.niceToHaveFeatures,
      timeline: templateData.timeline,
    };

    // Clear localStorage and submit
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
    onComplete(completeRequirements);
  };

  const totalSteps = getTotalSteps();
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Step {currentStep} of {totalSteps}</span>
          <span className="text-sm text-foreground-secondary">{Math.round(progress)}% complete</span>
        </div>
        <div className="h-2 bg-surface rounded-full overflow-hidden">
          <div
            className="h-full bg-accent transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Templates (Step 1 only) */}
      {currentStep === 1 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-4">Quick Start Templates</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(TEMPLATES).map(([key, template]) => (
              <button
                key={key}
                onClick={() => loadTemplate(key as keyof typeof TEMPLATES)}
                className="p-4 border border-border rounded-lg hover:border-accent hover:bg-accent-muted transition-colors text-left"
              >
                <div className="font-semibold text-foreground mb-1">{template.name}</div>
                <div className="text-sm text-foreground-secondary">{template.description}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 text-center text-sm text-foreground-secondary">
            Or fill out the questionnaire below for a custom recommendation
          </div>
        </div>
      )}

      {/* Step content */}
      <div className="bg-surface rounded-lg p-6 mb-6">
        {currentStep === 1 && <OrganizationStep requirements={requirements} updateRequirement={updateRequirement} />}
        {currentStep === 2 && <TimelineStep requirements={requirements} updateRequirement={updateRequirement} />}
        {currentStep === 3 && <TechnicalStep requirements={requirements} updateRequirement={updateRequirement} />}
        {currentStep === 4 && <PriorityWeightingStep requirements={requirements} updateRequirement={updateRequirement} />}
        {currentStep === 5 && <BudgetStep requirements={requirements} updateRequirement={updateRequirement} />}
        {currentStep === 6 && <OperationalStep requirements={requirements} updateRequirement={updateRequirement} />}
        {currentStep === 7 && <StrategicStep requirements={requirements} updateRequirement={updateRequirement} />}
        {currentStep === 8 && <ReviewStep requirements={requirements} />}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={prevStep}
          disabled={currentStep === 1}
          className="px-6 py-2 border border-border rounded-lg text-foreground hover:bg-cream/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Back
        </button>
        {currentStep < totalSteps ? (
          <button
            onClick={nextStep}
            className="px-6 py-2 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors"
          >
            Continue
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="px-8 py-2 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors"
          >
            Get Recommendations
          </button>
        )}
      </div>
    </div>
  );
}

// Step 1: Organization Context
function OrganizationStep({
  requirements,
  updateRequirement,
}: {
  requirements: Partial<UserRequirements>;
  updateRequirement: <K extends keyof UserRequirements>(key: K, value: UserRequirements[K]) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Organization Context</h2>
        <p className="text-foreground-secondary">Tell us about your organization and current setup</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Team Size</label>
        <select
          value={requirements.teamSize || ''}
          onChange={(e) => updateRequirement('teamSize', e.target.value as UserRequirements['teamSize'])}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select team size...</option>
          <option value="startup">Startup (1-10 people)</option>
          <option value="small">Small (11-50 people)</option>
          <option value="medium">Medium (51-200 people)</option>
          <option value="large">Large (201-1000 people)</option>
          <option value="enterprise">Enterprise (1000+ people)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Industry</label>
        <select
          value={requirements.industry || ''}
          onChange={(e) => updateRequirement('industry', e.target.value)}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select industry...</option>
          <option value="SaaS">SaaS</option>
          <option value="Finance">Finance</option>
          <option value="Healthcare">Healthcare</option>
          <option value="E-commerce">E-commerce</option>
          <option value="Media">Media</option>
          <option value="Gaming">Gaming</option>
          <option value="Enterprise">Enterprise</option>
          <option value="Government">Government</option>
          <option value="Education">Education</option>
          <option value="Technology">Technology</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Primary Use Cases (select all that apply)</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            'Web applications',
            'Mobile apps',
            'API services',
            'Data analytics',
            'Real-time features',
            'E-commerce',
            'Content management',
            'Microservices',
            'Machine learning',
            'IoT',
          ].map((useCase) => (
            <label key={useCase} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requirements.useCases?.includes(useCase) || false}
                onChange={(e) => {
                  const current = requirements.useCases || [];
                  if (e.target.checked) {
                    updateRequirement('useCases', [...current, useCase]);
                  } else {
                    updateRequirement('useCases', current.filter(u => u !== useCase));
                  }
                }}
                className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-ring focus:ring-2"
              />
              <span className="text-foreground/90 text-sm">{useCase}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Conditional: E-commerce specific questions */}
      {requirements.useCases?.includes('E-commerce') && (
        <div className="border-t border-border-muted pt-6 space-y-4">
          <h3 className="text-lg font-semibold text-accent">E-commerce Details</h3>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Expected Transaction Volume</label>
            <select
              value={requirements.transactionVolume || ''}
              onChange={(e) => updateRequirement('transactionVolume', e.target.value as UserRequirements['transactionVolume'])}
              className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
            >
              <option value="">Select volume...</option>
              <option value="low">Low (&lt; 1k transactions/month)</option>
              <option value="medium">Medium (1k-100k transactions/month)</option>
              <option value="high">High (100k-1M transactions/month)</option>
              <option value="very-high">Very High (1M+ transactions/month)</option>
            </select>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Current Stack (if migrating)</label>
        <p className="text-xs text-foreground-tertiary mb-2">Select technologies you currently use</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {['PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'React', 'Vue', 'Angular', 'Node.js', 'Python', 'Java', 'Go', 'Ruby', 'AWS', 'GCP', 'Azure', 'Kubernetes', 'Docker'].map((tech) => (
            <label key={tech} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requirements.currentStack?.includes(tech) || false}
                onChange={(e) => {
                  const current = requirements.currentStack || [];
                  if (e.target.checked) {
                    updateRequirement('currentStack', [...current, tech]);
                  } else {
                    updateRequirement('currentStack', current.filter(t => t !== tech));
                  }
                }}
                className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-ring focus:ring-2"
              />
              <span className="text-foreground/90 text-sm">{tech}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// Step 2: Timeline & Urgency
function TimelineStep({
  requirements,
  updateRequirement,
}: {
  requirements: Partial<UserRequirements>;
  updateRequirement: <K extends keyof UserRequirements>(key: K, value: UserRequirements[K]) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Timeline & Urgency</h2>
        <p className="text-foreground-secondary">When do you need this in production?</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Production Timeline</label>
        <div className="space-y-3">
          {[
            { value: 'very-urgent', label: '1 month', desc: 'Need to launch ASAP, favor mature tech' },
            { value: 'urgent', label: '3 months', desc: 'Fast-paced project, balance speed and features' },
            { value: 'moderate', label: '6 months', desc: 'Standard timeline, can afford proper setup' },
            { value: 'flexible', label: '12+ months', desc: 'Long-term project, can experiment with newer tech' },
          ].map((option) => (
            <label key={option.value} className="flex items-start space-x-3 cursor-pointer p-3 border border-border rounded-lg hover:border-accent hover:bg-accent-muted transition-colors">
              <input
                type="radio"
                name="timeline"
                value={option.value}
                checked={requirements.timeline === option.value}
                onChange={(e) => updateRequirement('timeline', e.target.value as UserRequirements['timeline'])}
                className="w-4 h-4 mt-1 text-accent bg-background border-border focus:ring-ring focus:ring-2"
              />
              <div>
                <div className="font-medium text-foreground">{option.label}</div>
                <div className="text-sm text-foreground-tertiary">{option.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-background/50 border border-border-muted rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <svg className="w-5 h-5 text-accent mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-foreground-secondary">
            <strong>Timeline Impact:</strong> Shorter timelines will favor mature, well-documented technologies with strong community support. Longer timelines allow for newer, cutting-edge options.
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 3: Technical Requirements
function TechnicalStep({
  requirements,
  updateRequirement,
}: {
  requirements: Partial<UserRequirements>;
  updateRequirement: <K extends keyof UserRequirements>(key: K, value: UserRequirements[K]) => void;
}) {
  const expertiseCategories = [
    { key: 'frontend', label: 'Frontend Frameworks', examples: 'React, Vue, Angular, Svelte' },
    { key: 'backend', label: 'Backend Languages', examples: 'Node.js, Python, Java, Go, Ruby' },
    { key: 'databases', label: 'Databases', examples: 'SQL, NoSQL, Graph databases' },
    { key: 'devops', label: 'DevOps/Infrastructure', examples: 'Kubernetes, Docker, CI/CD, Terraform' },
    { key: 'cloud', label: 'Cloud Platforms', examples: 'AWS, GCP, Azure, Cloudflare' },
  ];

  const updateExpertise = (category: string, level: string) => {
    const current = requirements.expertiseByCategory || {};
    updateRequirement('expertiseByCategory', {
      ...current,
      [category]: level as 'none' | 'beginner' | 'intermediate' | 'advanced' | 'expert',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Technical Requirements</h2>
        <p className="text-foreground-secondary">What are your technical needs and team expertise?</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Scalability Needs</label>
        <select
          value={requirements.scalabilityNeeds || ''}
          onChange={(e) => updateRequirement('scalabilityNeeds', e.target.value as UserRequirements['scalabilityNeeds'])}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select scalability needs...</option>
          <option value="low">Low (&lt; 10k users)</option>
          <option value="medium">Medium (10k-100k users)</option>
          <option value="high">High (100k-1M users)</option>
          <option value="very-high">Very High (1M+ users)</option>
        </select>
        <p className="mt-1 text-xs text-foreground-tertiary">How many users do you need to support?</p>
      </div>

      {/* Conditional: Multi-region deployment */}
      {requirements.scalabilityNeeds === 'very-high' && (
        <div className="border-t border-border-muted pt-6 space-y-4">
          <h3 className="text-lg font-semibold text-accent">High-Scale Requirements</h3>

          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requirements.multiRegion || false}
                onChange={(e) => updateRequirement('multiRegion', e.target.checked)}
                className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-ring focus:ring-2"
              />
              <span className="text-foreground/90">Require multi-region deployment</span>
            </label>
          </div>

          {requirements.multiRegion && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Target Regions</label>
              <div className="grid grid-cols-2 gap-3">
                {['North America', 'Europe', 'Asia Pacific', 'South America', 'Middle East', 'Africa'].map((region) => (
                  <label key={region} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={requirements.regions?.includes(region) || false}
                      onChange={(e) => {
                        const current = requirements.regions || [];
                        if (e.target.checked) {
                          updateRequirement('regions', [...current, region]);
                        } else {
                          updateRequirement('regions', current.filter(r => r !== region));
                        }
                      }}
                      className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-ring focus:ring-2"
                    />
                    <span className="text-foreground/90 text-sm">{region}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Performance Requirements</label>
        <select
          value={requirements.performanceNeeds || ''}
          onChange={(e) => updateRequirement('performanceNeeds', e.target.value as UserRequirements['performanceNeeds'])}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select performance needs...</option>
          <option value="low">Low (seconds acceptable)</option>
          <option value="medium">Medium (&lt; 1 second)</option>
          <option value="high">High (&lt; 100ms)</option>
          <option value="very-high">Very High (&lt; 10ms)</option>
        </select>
        <p className="mt-1 text-xs text-foreground-tertiary">How fast do responses need to be?</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Team Expertise by Category</label>
        <p className="text-xs text-foreground-tertiary mb-3">Rate your team&apos;s experience in each area</p>
        <div className="space-y-4">
          {expertiseCategories.map((category) => (
            <div key={category.key} className="border border-border-muted rounded-lg p-4">
              <div className="mb-2">
                <div className="font-medium text-foreground">{category.label}</div>
                <div className="text-xs text-foreground-tertiary">{category.examples}</div>
              </div>
              <select
                value={requirements.expertiseByCategory?.[category.key] || ''}
                onChange={(e) => updateExpertise(category.key, e.target.value)}
                className="w-full p-2 bg-background border border-border rounded-lg text-foreground text-sm focus:border-accent focus:outline-none"
              >
                <option value="">Select level...</option>
                <option value="none">None (no experience)</option>
                <option value="beginner">Beginner (learning)</option>
                <option value="intermediate">Intermediate (production experience)</option>
                <option value="advanced">Advanced (deep knowledge)</option>
                <option value="expert">Expert (can architect complex systems)</option>
              </select>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Overall Team Expertise Level</label>
        <select
          value={requirements.teamExpertise || ''}
          onChange={(e) => updateRequirement('teamExpertise', e.target.value as UserRequirements['teamExpertise'])}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select expertise level...</option>
          <option value="beginner">Beginner (learning new technologies)</option>
          <option value="intermediate">Intermediate (some production experience)</option>
          <option value="advanced">Advanced (deep technical knowledge)</option>
          <option value="expert">Expert (can handle complex systems)</option>
        </select>
        <p className="mt-1 text-xs text-foreground-tertiary">What&apos;s the general technical skill level of your team?</p>
      </div>
    </div>
  );
}

// Step 4: Priority Weighting
function PriorityWeightingStep({
  requirements,
  updateRequirement,
}: {
  requirements: Partial<UserRequirements>;
  updateRequirement: <K extends keyof UserRequirements>(key: K, value: UserRequirements[K]) => void;
}) {
  const priorities = [
    { key: 'cost-efficiency', label: 'Cost Efficiency', desc: 'Minimize infrastructure and licensing costs' },
    { key: 'performance', label: 'Performance', desc: 'Maximum speed and responsiveness' },
    { key: 'security-compliance', label: 'Security & Compliance', desc: 'Meet regulatory requirements and security standards' },
    { key: 'developer-experience', label: 'Developer Experience', desc: 'Easy to learn, maintain, and develop with' },
    { key: 'time-to-market', label: 'Time to Market', desc: 'Quick to implement and deploy' },
    { key: 'vendor-lock-in-avoidance', label: 'Vendor Lock-in Avoidance', desc: 'Maintain portability and flexibility' },
  ];

  const updatePriority = (key: string, value: number) => {
    const current = requirements.priorityWeights || {};
    updateRequirement('priorityWeights', {
      ...current,
      [key]: value,
    });
  };

  const getPriorityValue = (key: string): number => {
    return requirements.priorityWeights?.[key] || 3;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Priority Ranking</h2>
        <p className="text-foreground-secondary">How important is each factor for your decision?</p>
      </div>

      <div className="bg-background/50 border border-border-muted rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-2">
          <svg className="w-5 h-5 text-accent mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-foreground-secondary">
            Rate each factor from 1 (not important) to 5 (critical). These weights will customize your recommendations.
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {priorities.map((priority) => {
          const value = getPriorityValue(priority.key);
          return (
            <div key={priority.key} className="border border-border-muted rounded-lg p-4">
              <div className="mb-3">
                <div className="font-medium text-foreground">{priority.label}</div>
                <div className="text-sm text-foreground-tertiary">{priority.desc}</div>
              </div>

              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={value}
                  onChange={(e) => updatePriority(priority.key, parseInt(e.target.value))}
                  className="flex-1 h-2 bg-background rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, var(--color-orange) 0%, var(--color-orange) ${(value - 1) * 25}%, rgb(10, 30, 46) ${(value - 1) * 25}%, rgb(10, 30, 46) 100%)`,
                  }}
                />
                <div className="w-24 flex items-center justify-between">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button
                      key={i}
                      onClick={() => updatePriority(priority.key, i)}
                      className={`w-8 h-8 rounded-full border-2 text-sm font-semibold transition-all ${
                        value === i
                          ? 'border-accent bg-accent text-accent-foreground'
                          : 'border-border text-foreground-tertiary hover:border-border-muted0'
                      }`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-between text-xs text-foreground-tertiary mt-2">
                <span>Not Important</span>
                <span>Critical</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Step 5: Budget & Resources
function BudgetStep({
  requirements,
  updateRequirement,
}: {
  requirements: Partial<UserRequirements>;
  updateRequirement: <K extends keyof UserRequirements>(key: K, value: UserRequirements[K]) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Budget & Resources</h2>
        <p className="text-foreground-secondary">What&apos;s your budget and resource availability?</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Budget Level</label>
        <select
          value={requirements.budget || ''}
          onChange={(e) => updateRequirement('budget', e.target.value as UserRequirements['budget'])}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select budget level...</option>
          <option value="minimal">Minimal (&lt; $500/month)</option>
          <option value="moderate">Moderate ($500-$5k/month)</option>
          <option value="substantial">Substantial ($5k-$50k/month)</option>
          <option value="enterprise">Enterprise ($50k+/month)</option>
        </select>
        <p className="mt-1 text-xs text-foreground-tertiary">Monthly infrastructure budget</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Maintenance Capability</label>
        <select
          value={requirements.maintenanceCapability || ''}
          onChange={(e) => updateRequirement('maintenanceCapability', e.target.value as UserRequirements['maintenanceCapability'])}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select maintenance capability...</option>
          <option value="minimal">Minimal (need managed services)</option>
          <option value="moderate">Moderate (can handle basic ops)</option>
          <option value="high">High (dedicated ops team)</option>
        </select>
        <p className="mt-1 text-xs text-foreground-tertiary">How much operations work can your team handle?</p>
      </div>
    </div>
  );
}

// Step 6: Operational Preferences
function OperationalStep({
  requirements,
  updateRequirement,
}: {
  requirements: Partial<UserRequirements>;
  updateRequirement: <K extends keyof UserRequirements>(key: K, value: UserRequirements[K]) => void;
}) {
  const updateComplianceDetail = (category: string, detail: string, checked: boolean) => {
    const current = requirements.complianceDetails || {};
    const categoryDetails = current[category] || [];

    updateRequirement('complianceDetails', {
      ...current,
      [category]: checked
        ? [...categoryDetails, detail]
        : categoryDetails.filter(d => d !== detail),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Operational Preferences</h2>
        <p className="text-foreground-secondary">How do you want to operate your infrastructure?</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Hosting Preference</label>
        <select
          value={requirements.hostingPreference || ''}
          onChange={(e) => updateRequirement('hostingPreference', e.target.value as UserRequirements['hostingPreference'])}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select hosting preference...</option>
          <option value="self-hosted">Self-hosted (on-premise or VMs)</option>
          <option value="cloud">Cloud (IaaS like EC2)</option>
          <option value="managed">Managed (PaaS/SaaS)</option>
          <option value="no-preference">No preference</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Compliance Requirements</label>
        <div className="space-y-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={requirements.gdprRequired || false}
              onChange={(e) => updateRequirement('gdprRequired', e.target.checked)}
              className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-ring focus:ring-2"
            />
            <span className="text-foreground/90">GDPR (EU data protection)</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={requirements.hipaaRequired || false}
              onChange={(e) => updateRequirement('hipaaRequired', e.target.checked)}
              className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-ring focus:ring-2"
            />
            <span className="text-foreground/90">HIPAA (Healthcare data)</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={requirements.soc2Required || false}
              onChange={(e) => updateRequirement('soc2Required', e.target.checked)}
              className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-ring focus:ring-2"
            />
            <span className="text-foreground/90">SOC 2 (Security audit)</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={requirements.pciRequired || false}
              onChange={(e) => updateRequirement('pciRequired', e.target.checked)}
              className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-ring focus:ring-2"
            />
            <span className="text-foreground/90">PCI DSS (Payment card data)</span>
          </label>
        </div>
      </div>

      {/* Conditional: Healthcare compliance details */}
      {(requirements.industry === 'Healthcare' || requirements.hipaaRequired) && (
        <div className="border-t border-border-muted pt-6 space-y-4">
          <h3 className="text-lg font-semibold text-accent">Healthcare Compliance Details</h3>
          <div className="space-y-3">
            {['PHI storage', 'PHI transmission', 'BAA required', 'Audit trail required'].map((detail) => (
              <label key={detail} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requirements.complianceDetails?.['healthcare']?.includes(detail) || false}
                  onChange={(e) => updateComplianceDetail('healthcare', detail, e.target.checked)}
                  className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-ring focus:ring-2"
                />
                <span className="text-foreground/90 text-sm">{detail}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Conditional: Finance compliance details */}
      {(requirements.industry === 'Finance' || requirements.pciRequired) && (
        <div className="border-t border-border-muted pt-6 space-y-4">
          <h3 className="text-lg font-semibold text-accent">Financial Compliance Details</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">PCI DSS Level</label>
              <select
                value={requirements.complianceDetails?.['pci-level']?.[0] || ''}
                onChange={(e) => updateComplianceDetail('pci-level', e.target.value, true)}
                className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
              >
                <option value="">Select level...</option>
                <option value="Level 1">Level 1 (6M+ transactions/year)</option>
                <option value="Level 2">Level 2 (1M-6M transactions/year)</option>
                <option value="Level 3">Level 3 (20k-1M transactions/year)</option>
                <option value="Level 4">Level 4 (&lt; 20k transactions/year)</option>
              </select>
            </div>
            {['SOC 2 Type I', 'SOC 2 Type II', 'ISO 27001', 'FFIEC compliance'].map((detail) => (
              <label key={detail} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requirements.complianceDetails?.['finance']?.includes(detail) || false}
                  onChange={(e) => updateComplianceDetail('finance', detail, e.target.checked)}
                  className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-ring focus:ring-2"
                />
                <span className="text-foreground/90 text-sm">{detail}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Step 7: Strategic Considerations
function StrategicStep({
  requirements,
  updateRequirement,
}: {
  requirements: Partial<UserRequirements>;
  updateRequirement: <K extends keyof UserRequirements>(key: K, value: UserRequirements[K]) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Strategic Considerations</h2>
        <p className="text-foreground-secondary">What are your long-term priorities?</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Vendor Lock-in Tolerance</label>
        <select
          value={requirements.vendorLockInTolerance || ''}
          onChange={(e) => updateRequirement('vendorLockInTolerance', e.target.value as UserRequirements['vendorLockInTolerance'])}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select tolerance level...</option>
          <option value="none">None (must be portable)</option>
          <option value="low">Low (prefer portable)</option>
          <option value="medium">Medium (acceptable trade-off)</option>
          <option value="high">High (not a concern)</option>
        </select>
        <p className="mt-1 text-xs text-foreground-tertiary">How important is it to avoid vendor lock-in?</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Open Source Preference</label>
        <select
          value={requirements.openSourcePreference || ''}
          onChange={(e) => updateRequirement('openSourcePreference', e.target.value as UserRequirements['openSourcePreference'])}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select preference...</option>
          <option value="required">Required (must be open source)</option>
          <option value="preferred">Preferred (open source preferred)</option>
          <option value="neutral">Neutral (no preference)</option>
          <option value="commercial-ok">Commercial OK (prefer paid support)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Innovation vs Stability</label>
        <select
          value={requirements.innovationVsStability || ''}
          onChange={(e) => updateRequirement('innovationVsStability', e.target.value as UserRequirements['innovationVsStability'])}
          className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
        >
          <option value="">Select preference...</option>
          <option value="bleeding-edge">Bleeding Edge (newest tech)</option>
          <option value="modern">Modern (proven but current)</option>
          <option value="stable">Stable (battle-tested)</option>
          <option value="conservative">Conservative (mature only)</option>
        </select>
        <p className="mt-1 text-xs text-foreground-tertiary">How important is cutting-edge tech vs proven stability?</p>
      </div>
    </div>
  );
}

// Step 8: Review & Submit
function ReviewStep({ requirements }: { requirements: Partial<UserRequirements> }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Review Your Answers</h2>
        <p className="text-foreground-secondary">Check your answers before getting recommendations</p>
      </div>

      <div className="space-y-4">
        <div className="border-b border-border-muted pb-3">
          <h3 className="text-sm font-semibold text-accent mb-2">Organization</h3>
          <div className="space-y-1 text-sm">
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Team Size:</span> {requirements.teamSize || 'Not set'}</p>
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Industry:</span> {requirements.industry || 'Not set'}</p>
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Use Cases:</span> {requirements.useCases?.join(', ') || 'None selected'}</p>
            {requirements.currentStack && requirements.currentStack.length > 0 && (
              <p className="text-foreground/90"><span className="text-foreground-tertiary">Current Stack:</span> {requirements.currentStack.join(', ')}</p>
            )}
            {requirements.timeline && (
              <p className="text-foreground/90"><span className="text-foreground-tertiary">Timeline:</span> {requirements.timeline}</p>
            )}
          </div>
        </div>

        <div className="border-b border-border-muted pb-3">
          <h3 className="text-sm font-semibold text-accent mb-2">Technical</h3>
          <div className="space-y-1 text-sm">
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Scalability:</span> {requirements.scalabilityNeeds || 'Not set'}</p>
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Performance:</span> {requirements.performanceNeeds || 'Not set'}</p>
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Team Expertise:</span> {requirements.teamExpertise || 'Not set'}</p>
            {requirements.multiRegion && (
              <p className="text-foreground/90"><span className="text-foreground-tertiary">Multi-region:</span> {requirements.regions?.join(', ') || 'Yes'}</p>
            )}
            {requirements.expertiseByCategory && Object.keys(requirements.expertiseByCategory).length > 0 && (
              <div className="mt-2">
                <span className="text-foreground-tertiary">Expertise by Category:</span>
                <div className="ml-4 mt-1 space-y-1">
                  {Object.entries(requirements.expertiseByCategory).map(([key, value]) => (
                    <p key={key} className="text-foreground-secondary text-xs">
                      {key}: {value}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {requirements.priorityWeights && Object.keys(requirements.priorityWeights).length > 0 && (
          <div className="border-b border-border-muted pb-3">
            <h3 className="text-sm font-semibold text-accent mb-2">Priority Weights</h3>
            <div className="space-y-1 text-sm">
              {Object.entries(requirements.priorityWeights).map(([key, value]) => (
                <p key={key} className="text-foreground/90">
                  <span className="text-foreground-tertiary">{key.replace(/-/g, ' ')}:</span> {value}/5
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="border-b border-border-muted pb-3">
          <h3 className="text-sm font-semibold text-accent mb-2">Budget & Resources</h3>
          <div className="space-y-1 text-sm">
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Budget:</span> {requirements.budget || 'Not set'}</p>
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Maintenance:</span> {requirements.maintenanceCapability || 'Not set'}</p>
          </div>
        </div>

        <div className="border-b border-border-muted pb-3">
          <h3 className="text-sm font-semibold text-accent mb-2">Operational</h3>
          <div className="space-y-1 text-sm">
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Hosting:</span> {requirements.hostingPreference || 'Not set'}</p>
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Compliance:</span> {[
              requirements.gdprRequired && 'GDPR',
              requirements.hipaaRequired && 'HIPAA',
              requirements.soc2Required && 'SOC 2',
              requirements.pciRequired && 'PCI DSS',
            ].filter(Boolean).join(', ') || 'None required'}</p>
            {requirements.complianceDetails && Object.keys(requirements.complianceDetails).length > 0 && (
              <div className="mt-2">
                <span className="text-foreground-tertiary">Compliance Details:</span>
                <div className="ml-4 mt-1 space-y-1">
                  {Object.entries(requirements.complianceDetails).map(([key, values]) => (
                    <p key={key} className="text-foreground-secondary text-xs">
                      {key}: {Array.isArray(values) ? values.join(', ') : values}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-b border-border-muted pb-3">
          <h3 className="text-sm font-semibold text-accent mb-2">Strategic</h3>
          <div className="space-y-1 text-sm">
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Vendor Lock-in:</span> {requirements.vendorLockInTolerance || 'Not set'}</p>
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Open Source:</span> {requirements.openSourcePreference || 'Not set'}</p>
            <p className="text-foreground/90"><span className="text-foreground-tertiary">Innovation:</span> {requirements.innovationVsStability || 'Not set'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
